// src/hooks/useAuthLogic.ts
import { useState, useEffect } from "react"
import { apiFetch } from "../services/api"

/**
 * User shape coming from backend
 */
export type User = {
  id: number
  email: string
  first_name?: string
  last_name?: string
  city?: string
  dob?: string
  role: "student" | "admin"
  trial_end?: string
  is_paid?: boolean
}

/**
 * Auth logic hook
 * Handles:
 * - token persistence
 * - login / register
 * - current user
 */
export const useAuthLogic = () => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  /**
   * Load user on first mount if token exists
   */
  useEffect(() => {
    if (typeof window === "undefined") return
    const token = localStorage.getItem("token")

    // No token → not logged in
    if (!token) {
      setLoading(false)
      return
    }

    apiFetch("/auth/me")
      .then(data => {
        if (data) setUser(data)
      })
      .finally(() => setLoading(false))
  }, [])

  /**
   * Login user
   */
  const login = async (email: string, password: string) => {
    const data = await apiFetch("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    })

    if (!data?.token || !data?.user) throw new Error("Invalid server response")

    if (typeof window !== "undefined") {
      localStorage.setItem("token", data.token)
    }
    setUser(data.user)
  }

  /**
   * Register user (free trial)
   */
  const register = async (
    email: string,
    password: string,
    profile: {
      first_name?: string
      last_name?: string
      city?: string
      dob?: string
    } = {}
  ) => {
    const data = await apiFetch("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, ...profile }),
    })

    if (!data?.token || !data?.user) throw new Error("Invalid server response")

    if (typeof window !== "undefined") {
      localStorage.setItem("token", data.token)
    }
    setUser(data.user)
  }

  /**
   * Logout
   */
  const logout = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("token")
    }
    setUser(null)
  }

  const updateProfile = (updates: Partial<User>) => {
    setUser(prev => (prev ? { ...prev, ...updates } : prev))
  }

  return {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
  }
}
